import React, { useEffect, useMemo, useState } from "react";
import Footer from "../../../../core/common/Footer";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import useApi from "../../../../core/customhook/useApi";
import Swal from "sweetalert2";
import z from "zod";
import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
import webservice_url from "../../../../core/webservice/Webservice_url";

const required = "This field is required.";

export const updatePackageSchema = z.object({
  package_name: z.string().trim().min(1, required),
  package_code: z.string().trim().min(1, required),
  geo_id: z.string().trim().min(1, required),
});

// Only one of these can be selected at a time (also the display order)
const EMPLOYMENT_CHECK_IDS = [
  "b225216b-ec67-4b70-a4f0-61ce4460b8bb", // Employment
  "1f12f689-b17a-409b-b433-bc7d685ca84f", // Employment - 2 Year
  "2c825e1d-10d2-4caf-b12d-b1df9c21b806", // Employment - 3 Year
  "1eda7972-29b4-40d4-a688-44a3afc2c360", // Employment - 5 Year
  "d904e066-1c35-4525-b971-7aaaa8cbde44", // Employment - 7 Year
  "a0057787-887b-4d45-9da3-5a758e4b6964", // Employment - 10 Year
];

const EDUCATION_CHECK_IDS = [
  "550f294a-257d-4b87-a0e7-8b34a3223d41", // 2 Highest Education
  "66b2e258-615e-4998-b51b-b8b7ec5a2417", // All Education Documents
  "1d479262-b776-4bb5-8e52-3a6919556f5d", // Education Documents
  "a687f91f-1c4c-4761-a4fe-37a564887783", // Highest Education
];

const PANCARD_CHECK_IDS = [
  "8ff47bc3-a23c-455a-9970-4fff6a685edd",
  "12c919a4-0f58-404a-b147-5a8aea9ab00b",
];

const EditPackage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const packageId = location.state?.packageId;

  const [formData, setFormData] = useState({
    package_name: "",
    package_code: "",
    geo_id: "",
  });

  const [countries, setCountries] = useState<any[]>([]);
  const [checkHeaders, setCheckHeaders] = useState<any[]>([]);
  const [packageChecks, setPackageChecks] = useState<any[]>([]);
  const [selectedChecks, setSelectedChecks] = useState<string[]>([]);
  const [originalSop, setOriginalSop] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const getPackageById = useApi<any>(`/api/v1/admin/packages/${packageId}`, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const getTemplateByPackageId = useApi<any>(
    `/api/v1/admin/packages/${packageId}/templates`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const updatePackage = useApi<any>(
    `/api/v1/admin/update-package-master/${packageId}`,
    {
      method: "PATCH",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getCountries = useApi<any>(webservice_url.countries, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const getCheckHeader = useApi<any>("/api/v1/admin/check-headers", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const checkTypeMap = useMemo(() => {
    const map: Record<string, string> = {};
    checkHeaders
      .flatMap((header: any) => header.checks ?? [])
      .forEach((c: any) => {
        map[c.check_id] = c.check_type;
      });
    return map;
  }, [checkHeaders]);

  const nonAssistedChecks = useMemo(
    () =>
      packageChecks.filter(
        (c: any) => checkTypeMap[c.check_id] === "NON-ASSISTED",
      ),
    [packageChecks, checkTypeMap],
  );

  const assistedChecks = useMemo(
    () =>
      packageChecks.filter(
        (c: any) => checkTypeMap[c.check_id] !== "NON-ASSISTED",
      ),
    [packageChecks, checkTypeMap],
  );

  // Employment checks, shown as their own row (in EMPLOYMENT_CHECK_IDS order)
  const employmentChecks = useMemo(
    () =>
      EMPLOYMENT_CHECK_IDS.map((id) =>
        assistedChecks.find((c: any) => c.check_id === id),
      ).filter(Boolean),
    [assistedChecks],
  );

  // Education checks, shown as their own row (in EDUCATION_CHECK_IDS order)
  const educationChecks = useMemo(
    () =>
      EDUCATION_CHECK_IDS.map((id) =>
        assistedChecks.find((c: any) => c.check_id === id),
      ).filter(Boolean),
    [assistedChecks],
  );

  // Remaining assisted checks (PAN, Aadhar, Address, Career Gap...) stay in the normal grid
  const otherAssistedChecks = useMemo(
    () =>
      assistedChecks.filter(
        (c: any) =>
          !EMPLOYMENT_CHECK_IDS.includes(c.check_id) &&
          !EDUCATION_CHECK_IDS.includes(c.check_id),
      ),
    [assistedChecks],
  );

  // One continuous grid, in sequence: Employment, then Education, then the rest
  const orderedAssistedChecks = useMemo(
    () => [...employmentChecks, ...educationChecks, ...otherAssistedChecks],
    [employmentChecks, educationChecks, otherAssistedChecks],
  );

  const newlyAddedChecks = useMemo(
    () =>
      packageChecks.filter(
        (c: any) =>
          !c.assign && !c.checked && selectedChecks.includes(c.check_id),
      ),
    [packageChecks, selectedChecks],
  );

  const sop = useMemo(
    () =>
      [originalSop, ...newlyAddedChecks.map((c: any) => c.check_name)]
        .filter(Boolean)
        .join(" + "),
    [originalSop, newlyAddedChecks],
  );

  const loadData = async () => {
    try {
      setLoading(true);
      const [countryRes, packageRes, templateRes, headerRes] =
        await Promise.all([
          getCountries.callApi(),
          getPackageById.callApi(),
          getTemplateByPackageId.callApi(),
          getCheckHeader.callApi(),
        ]);

      if (countryRes?.success) {
        setCountries(countryRes.data);
      }

      if (headerRes?.success) {
        setCheckHeaders(headerRes.data);
      }

      if (packageRes?.success) {
        setOriginalSop(packageRes.data[0].sop ?? "");

        setFormData({
          package_name: packageRes.data[0].package_name,
          package_code: packageRes.data[0].package_code,
          geo_id: packageRes.data[0].geo_id,
        });
      }

      if (templateRes?.success) {
        setPackageChecks(templateRes.data);

        setSelectedChecks(
          templateRes.data
            .filter((item: any) => item.assign && item.checked)
            .map((item: any) => item.check_id),
        );
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to load package.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (packageId) {
      loadData();
    }
  }, [packageId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleCheckToggle = (check: any) => {
    if (check.assign || check.checked) return;

    setSelectedChecks((prev) => {
      if (EMPLOYMENT_CHECK_IDS.includes(check.check_id)) {
        if (prev.includes(check.check_id)) {
          return prev.filter((id) => id !== check.check_id);
        }

        return [
          ...prev.filter((id) => !EMPLOYMENT_CHECK_IDS.includes(id)),
          check.check_id,
        ];
      }

      if (EDUCATION_CHECK_IDS.includes(check.check_id)) {
        if (prev.includes(check.check_id)) {
          return prev.filter((id) => id !== check.check_id);
        }

        return [
          ...prev.filter((id) => !EDUCATION_CHECK_IDS.includes(id)),
          check.check_id,
        ];
      }

      if (PANCARD_CHECK_IDS.includes(check.check_id)) {
        if (prev.includes(check.check_id)) {
          return prev.filter((id) => id !== check.check_id);
        }

        return [
          ...prev.filter((id) => !PANCARD_CHECK_IDS.includes(id)),
          check.check_id,
        ];
      }

      return prev.includes(check.check_id)
        ? prev.filter((id) => id !== check.check_id)
        : [...prev, check.check_id];
    });
  };

  const handleSubmit = async () => {
    const validation = updatePackageSchema.safeParse(formData);
    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};

      validation.error.issues.forEach((issue) => {
        fieldErrors[issue.path[0] as string] = issue.message;
      });

      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    try {
      const updatePayload = {
        package_name: formData.package_name,
        package_code: formData.package_code,
        geo_id: formData.geo_id,
        sop,
        check_ids: newlyAddedChecks.map((item: any) => item.check_id),
      };

      const updateRes = await updatePackage.callApi(updatePayload);
      if (!updateRes?.success) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            updateRes?.error ||
            updateRes?.message ||
            "Unable to update package.",
        });
        return;
      }
      Swal.fire({
        icon: "success",
        title: "Success",
        text: updateRes.message || "Package updated successfully.",
      });

      navigate(-1);
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to update package.",
      });
    }
  };

  /* ---------- Renderers ---------- */

  const renderCheckCard = (check: any) => {
    const selected = selectedChecks.includes(check.check_id);
    const isLocked = !!(check.assign || check.checked);
    const isAttached = isLocked && selected;
    const isLockedUnchecked = isLocked && !selected;
    const isNew = !isLocked && selected;

    const backgroundColor = isAttached
      ? "#E8F8EF"
      : isLockedUnchecked
        ? "#F5F5F5"
        : isNew
          ? "#EEF6FF"
          : "#fff";

    const borderColor = isAttached
      ? "1px solid #198754"
      : isLockedUnchecked
        ? "1px solid #DADADA"
        : isNew
          ? "1px solid #0d6efd"
          : "1px solid #E5E3DC";

    const titleColor = isAttached
      ? "#198754"
      : isLockedUnchecked
        ? "#8A8A8A"
        : isNew
          ? "#0d6efd"
          : "#2C2C2A";

    return (
      <div key={check.check_id} className="col-xl-3 col-lg-4 col-md-6 mb-3">
        <div
          onClick={() => handleCheckToggle(check)}
          className="rounded-3 px-3 py-3 h-100"
          style={{
            cursor: isLocked ? "not-allowed" : "pointer",
            transition: "all .2s ease",
            backgroundColor,
            border: borderColor,
            opacity: isLockedUnchecked ? 0.75 : isAttached ? 0.9 : 1,
          }}
        >
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h6
                className="mb-1 fw-semibold"
                style={{
                  color: titleColor,
                  fontSize: "15px",
                }}
              >
                {check.check_name}
              </h6>

              {isAttached && (
                <small
                  className="fw-semibold"
                  style={{ color: "#198754", fontSize: "11px" }}
                >
                  ✓ Already Attached
                </small>
              )}

              {isNew && (
                <small
                  className="fw-semibold"
                  style={{ color: "#0d6efd", fontSize: "11px" }}
                >
                  + New Check
                </small>
              )}
            </div>

            <input
              type="checkbox"
              className="form-check-input"
              checked={selected}
              disabled={isLocked}
              onClick={(e) => e.stopPropagation()}
              onChange={() => handleCheckToggle(check)}
              style={{
                width: 18,
                height: 18,
                cursor: isLocked ? "not-allowed" : "pointer",
                accentColor: isAttached ? "#198754" : "#0d6efd",
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderGroup = (
    title: string,
    subtitle: string,
    list: any[],
    topSections?: React.ReactNode,
    gridList: any[] = list,
  ) => {
    if (!list.length) return null;

    // Count uses the full list; the normal grid uses gridList
    const selectedCount = list.filter((c: any) =>
      selectedChecks.includes(c.check_id),
    ).length;

    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="fw-bold mb-1">{title}</h5>
            <small className="text-muted">{subtitle}</small>
          </div>

          <span
            className="badge px-3 py-2"
            style={{
              background: selectedCount ? "#0F6E56" : "#E1F5EE",
              color: selectedCount ? "#fff" : "#085041",
              fontSize: "12px",
            }}
          >
            {selectedCount}/{list.length} Selected
          </span>
        </div>

        {topSections}

        <div className="row">{gridList.map(renderCheckCard)}</div>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Update Package</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">Account Configuration</li>
                <li className="breadcrumb-item">Package</li>
                <li className="breadcrumb-item active">Update Package</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>
        {loading ? (
          <SnipperLoader />
        ) : (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Update Package</h5>
            </div>

            <div className="card-body">
              <div className="row">
                {/* Package Name */}
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Package Name <span className="text-danger">*</span>
                  </label>

                  <input
                    name="package_name"
                    value={formData.package_name}
                    onChange={handleChange}
                    className={`form-control ${errors.package_name ? "is-invalid" : ""}`}
                  />

                  {errors.package_name && (
                    <div className="invalid-feedback">
                      {errors.package_name}
                    </div>
                  )}
                </div>

                {/* Package Code */}
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Package Code <span className="text-danger">*</span>
                  </label>

                  <input
                    name="package_code"
                    value={formData.package_code}
                    onChange={handleChange}
                    className={`form-control ${errors.package_code ? "is-invalid" : ""}`}
                  />

                  {errors.package_code && (
                    <div className="invalid-feedback">
                      {errors.package_code}
                    </div>
                  )}
                </div>

                {/* GEO */}
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    GEO
                    <span className="text-danger">*</span>
                  </label>

                  <select
                    name="geo_id"
                    value={formData.geo_id}
                    onChange={handleChange}
                    className={`form-select ${errors.geo_id ? "is-invalid" : ""}`}
                  >
                    <option value="">Select GEO</option>

                    {countries.map((item: any) => (
                      <option key={item.country_id} value={item.country_id}>
                        {item.country_name}
                      </option>
                    ))}
                  </select>
                  {errors.geo_id && (
                    <div className="invalid-feedback">{errors.geo_id}</div>
                  )}
                </div>

                {/* Scope of Package */}
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Scope of Package
                    <span className="text-danger">*</span>
                  </label>

                  <input value={sop} disabled className="form-control" />
                </div>
              </div>

              <hr
                style={{
                  border: "none",
                  height: "2px",
                  background:
                    "linear-gradient(to right, transparent, #6d3300, #af5d00, #f26522, #f26522, #6d3300, transparent)",
                  borderRadius: "999px",
                  opacity: 1,
                  marginTop: "30px",
                  marginBottom: "30px",
                }}
              />

              <div>
                <h5 className="fw-bold mb-1">Package Checks</h5>
                <small className="text-muted">
                  Locked checks cannot be changed. You can only add new checks.
                </small>

                <hr
                  style={{
                    border: "none",
                    borderTop: "1.5px solid #6c757d",
                    marginTop: "10px",
                    marginBottom: "10px",
                  }}
                />

                <div className="card-body">
                  {renderGroup(
                    "Non-Assisted Checks",
                    "Locked checks are disabled. Select additional non-assisted checks.",
                    nonAssistedChecks,
                  )}
                  {renderGroup(
                    "Assisted Checks",
                    "Locked checks are disabled. Select additional assisted checks.",
                    assistedChecks,
                    undefined,
                    orderedAssistedChecks,
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => navigate(all_routes.package)}
                >
                  Back
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default EditPackage;


// import React, { useEffect, useMemo, useState } from "react";
// import Footer from "../../../../core/common/Footer";
// import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { all_routes } from "../../../router/all_routes";
// import useApi from "../../../../core/customhook/useApi";
// import Swal from "sweetalert2";
// import z from "zod";
// import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
// import webservice_url from "../../../../core/webservice/Webservice_url";

// const required = "This field is required.";

// export const updatePackageSchema = z.object({
//   package_name: z.string().trim().min(1, required),
//   package_code: z.string().trim().min(1, required),
//   geo_id: z.string().trim().min(1, required),
// });

// const EditPackage = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const packageId = location.state?.packageId;

//   const [formData, setFormData] = useState({
//     package_name: "",
//     package_code: "",
//     geo_id: "",
//   });

//   const [countries, setCountries] = useState<any[]>([]); 
//   const [checkHeaders, setCheckHeaders] = useState<any[]>([]); 
//   const [packageChecks, setPackageChecks] = useState<any[]>([]); 
//   const [selectedChecks, setSelectedChecks] = useState<string[]>([]); 
//   const [originalSop, setOriginalSop] = useState(""); 
//   const [errors, setErrors] = useState<Record<string, string>>({}); 
//   const [loading, setLoading] = useState(false); 
//   const getPackageById = useApi<any>(`/api/v1/admin/packages/${packageId}`, {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const getTemplateByPackageId = useApi<any>(
//     `/api/v1/admin/packages/${packageId}/templates`,
//     {
//       method: "GET",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );

//   const updatePackage = useApi<any>(
//     `/api/v1/admin/update-package-master/${packageId}`,
//     {
//       method: "PATCH",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );

//   const getCountries = useApi<any>(webservice_url.countries, {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const getCheckHeader = useApi<any>("/api/v1/admin/check-headers", {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//   });
 
// const EMPLOYMENT_CHECK_IDS = [
//   "b225216b-ec67-4b70-a4f0-61ce4460b8bb",
//   "2c825e1d-10d2-4caf-b12d-b1df9c21b806",
//   "1eda7972-29b4-40d4-a688-44a3afc2c360",
//   "d904e066-1c35-4525-b971-7aaaa8cbde44",
// ];

// const EDUCATION_CHECK_IDS = [
//  "550f294a-257d-4b87-a0e7-8b34a3223d41",
//   "a687f91f-1c4c-4761-a4fe-37a564887783",
//   "66b2e258-615e-4998-b51b-b8b7ec5a2417"
// ];

// const PANCARD_CHECK_IDS = [
//   "8ff47bc3-a23c-455a-9970-4fff6a685edd",
//   "12c919a4-0f58-404a-b147-5a8aea9ab00b",
// ]; 

//   const checkTypeMap = useMemo(() => {
//     const map: Record<string, string> = {};
//     checkHeaders
//       .flatMap((header: any) => header.checks ?? [])
//       .forEach((c: any) => {
//         map[c.check_id] = c.check_type;
//       });
//     return map;
//   }, [checkHeaders]);

//   const nonAssistedChecks = useMemo(
//     () =>
//       packageChecks.filter(
//         (c: any) => checkTypeMap[c.check_id] === "NON-ASSISTED",
//       ),
//     [packageChecks, checkTypeMap],
//   );
 
//   const assistedChecks = useMemo(
//     () =>
//       packageChecks.filter(
//         (c: any) => checkTypeMap[c.check_id] !== "NON-ASSISTED",
//       ),
//     [packageChecks, checkTypeMap],
//   );
 
//   const newlyAddedChecks = useMemo(
//     () =>
//       packageChecks.filter(
//         (c: any) =>
//           !c.assign && !c.checked && selectedChecks.includes(c.check_id),
//       ),
//     [packageChecks, selectedChecks],
//   );
 
//   const sop = useMemo(
//     () =>
//       [originalSop, ...newlyAddedChecks.map((c: any) => c.check_name)]
//         .filter(Boolean)
//         .join(" + "),
//     [originalSop, newlyAddedChecks],
//   ); 

//   const loadData = async () => {
//     try {
//       setLoading(true);
//       const [countryRes, packageRes, templateRes, headerRes] =
//         await Promise.all([
//           getCountries.callApi(),
//           getPackageById.callApi(),
//           getTemplateByPackageId.callApi(),
//           getCheckHeader.callApi(),
//         ]);

//       if (countryRes?.success) {
//         setCountries(countryRes.data);
//       }

//       if (headerRes?.success) {
//         setCheckHeaders(headerRes.data);
//       }

//       if (packageRes?.success) {
//         setOriginalSop(packageRes.data[0].sop ?? "");

//         setFormData({
//           package_name: packageRes.data[0].package_name,
//           package_code: packageRes.data[0].package_code,
//           geo_id: packageRes.data[0].geo_id,
//         });
//       }

//       if (templateRes?.success) {
//         setPackageChecks(templateRes.data);
 
//         setSelectedChecks(
//           templateRes.data
//             .filter((item: any) => item.assign && item.checked)
//             .map((item: any) => item.check_id),
//         );
//       }
//     } catch (error) {
//       Swal.fire({
//         icon: "error",
//         title: "Error",
//         text: "Unable to load package.",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (packageId) {
//       loadData();
//     }
//   }, [packageId]);

//   const handleChange = (
//     e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
//   ) => {
//     const { name, value } = e.target;

//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));

//     setErrors((prev) => ({
//       ...prev,
//       [name]: "",
//     }));
//   };

//  const handleCheckToggle = (check: any) => { 
//   if (check.assign || check.checked) return;

//   setSelectedChecks((prev) => { 
//     if (EMPLOYMENT_CHECK_IDS.includes(check.check_id)) {
//       if (prev.includes(check.check_id)) {
//         return prev.filter((id) => id !== check.check_id);
//       }

//       return [
//         ...prev.filter((id) => !EMPLOYMENT_CHECK_IDS.includes(id)),
//         check.check_id,
//       ];
//     }
 
//     if (EDUCATION_CHECK_IDS.includes(check.check_id)) {
//       if (prev.includes(check.check_id)) {
//         return prev.filter((id) => id !== check.check_id);
//       }

//       return [
//         ...prev.filter((id) => !EDUCATION_CHECK_IDS.includes(id)),
//         check.check_id,
//       ];
//     }
 
//     if (PANCARD_CHECK_IDS.includes(check.check_id)) {
//       if (prev.includes(check.check_id)) {
//         return prev.filter((id) => id !== check.check_id);
//       }

//       return [
//         ...prev.filter((id) => !PANCARD_CHECK_IDS.includes(id)),
//         check.check_id,
//       ];
//     }
 
//     return prev.includes(check.check_id)
//       ? prev.filter((id) => id !== check.check_id)
//       : [...prev, check.check_id];
//   });
// }; 
//   const handleSubmit = async () => {
//     const validation = updatePackageSchema.safeParse(formData);
//     if (!validation.success) {
//       const fieldErrors: Record<string, string> = {};

//       validation.error.issues.forEach((issue) => {
//         fieldErrors[issue.path[0] as string] = issue.message;
//       });

//       setErrors(fieldErrors);
//       return;
//     }

//     setErrors({});
//     try {
//       const updatePayload = {
//         package_name: formData.package_name,
//         package_code: formData.package_code,
//         geo_id: formData.geo_id,
//         sop,
//         check_ids: newlyAddedChecks.map((item: any) => item.check_id),
//       };

//       const updateRes = await updatePackage.callApi(updatePayload); 
//       if (!updateRes?.success) {
//         Swal.fire({
//           icon: "error",
//           title: "Error",
//           text:
//             updateRes?.error ||
//             updateRes?.message ||
//             "Unable to update package.",
//         });
//         return;
//       } 
//       Swal.fire({
//         icon: "success",
//         title: "Success",
//         text: updateRes.message || "Package updated successfully.",
//       });

//       navigate(-1);
//     } catch (error: any) {
//       Swal.fire({
//         icon: "error",
//         title: "Error",
//         text:
//           error?.response?.data?.error ||
//           error?.response?.data?.message ||
//           error?.message ||
//           "Unable to update package.",
//       });
//     }
//   }; 
//   const renderGroup = (title: string, subtitle: string, list: any[]) => {
//     if (!list.length) return null;

//     const selectedCount = list.filter((c: any) =>
//       selectedChecks.includes(c.check_id),
//     ).length;

//     return (
//       <div className="mb-4">
//         <div className="d-flex justify-content-between align-items-center mb-3">
//           <div>
//             <h5 className="fw-bold mb-1">{title}</h5>
//             <small className="text-muted">{subtitle}</small>
//           </div>

//           <span
//             className="badge px-3 py-2"
//             style={{
//               background: selectedCount ? "#0F6E56" : "#E1F5EE",
//               color: selectedCount ? "#fff" : "#085041",
//               fontSize: "12px",
//             }}
//           >
//             {selectedCount}/{list.length} Selected
//           </span>
//         </div>

//         <div className="row">
//           {list.map((check: any) => {
//             const selected = selectedChecks.includes(check.check_id); 
//             const isLocked = !!(check.assign || check.checked); 
//             const isAttached = isLocked && selected;  
//             const isLockedUnchecked = isLocked && !selected; 
//             const isNew = !isLocked && selected;

//             const backgroundColor = isAttached
//               ? "#E8F8EF"
//               : isLockedUnchecked
//                 ? "#F5F5F5"
//                 : isNew
//                   ? "#EEF6FF"
//                   : "#fff";

//             const borderColor = isAttached
//               ? "1px solid #198754"
//               : isLockedUnchecked
//                 ? "1px solid #DADADA"
//                 : isNew
//                   ? "1px solid #0d6efd"
//                   : "1px solid #E5E3DC";

//             const titleColor = isAttached
//               ? "#198754"
//               : isLockedUnchecked
//                 ? "#8A8A8A"
//                 : isNew
//                   ? "#0d6efd"
//                   : "#2C2C2A";

//             return (
//               <div
//                 key={check.check_id}
//                 className="col-xl-3 col-lg-4 col-md-6 mb-3"
//               >
//                 <div
//                   onClick={() => handleCheckToggle(check)}
//                   className="rounded-3 px-3 py-3 h-100"
//                   style={{
//                     cursor: isLocked ? "not-allowed" : "pointer",
//                     transition: "all .2s ease",
//                     backgroundColor,
//                     border: borderColor,
//                     opacity: isLockedUnchecked ? 0.75 : isAttached ? 0.9 : 1,
//                   }}
//                 >
//                   <div className="d-flex justify-content-between align-items-start">
//                     <div>
//                       <h6
//                         className="mb-1 fw-semibold"
//                         style={{
//                           color: titleColor,
//                           fontSize: "15px",
//                         }}
//                       >
//                         {check.check_name}
//                       </h6>

//                       {isAttached && (
//                         <small
//                           className="fw-semibold"
//                           style={{ color: "#198754", fontSize: "11px" }}
//                         >
//                           ✓ Already Attached
//                         </small>
//                       )}

//                       {isNew && (
//                         <small
//                           className="fw-semibold"
//                           style={{ color: "#0d6efd", fontSize: "11px" }}
//                         >
//                           + New Check
//                         </small>
//                       )}
//                     </div>

//                     <input
//                       type="checkbox"
//                       className="form-check-input"
//                       checked={selected}
//                       disabled={isLocked}
//                       onClick={(e) => e.stopPropagation()}
//                       onChange={() => handleCheckToggle(check)}
//                       style={{
//                         width: 18,
//                         height: 18,
//                         cursor: isLocked ? "not-allowed" : "pointer",
//                         accentColor: isAttached ? "#198754" : "#0d6efd",
//                       }}
//                     />
//                   </div>
//                 </div>
//               </div>
//             );
//           })}
//         </div>
//       </div>
//     );
//   };

//   return (
//     <div className="page-wrapper">
//       <div className="content">
//         {/* Breadcrumb */}
//         <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
//           <div className="my-auto mb-2">
//             <h2 className="mb-1">Update Package</h2>
//             <nav>
//               <ol className="breadcrumb mb-0">
//                 <li className="breadcrumb-item">
//                   <Link to={all_routes.superAdminDashboard}>
//                     <i className="ti ti-smart-home" />
//                   </Link>
//                 </li>
//                 <li className="breadcrumb-item">Account Activation</li>
//                 <li className="breadcrumb-item">Account Configuration</li>
//                 <li className="breadcrumb-item">Package</li>
//                 <li className="breadcrumb-item active">Update Package</li>
//               </ol>
//             </nav>
//           </div>
//           <div className="head-icons ms-2">
//             <CollapseHeader />
//           </div>
//         </div>
//         {loading ? (
//           <SnipperLoader />
//         ) : (
//           <div className="card">
//             <div className="card-header">
//               <h5 className="mb-0">Update Package</h5>
//             </div>

//             <div className="card-body">
//               <div className="row">
//                 {/* Package Name */}
//                 <div className="col-md-4 mb-3">
//                   <label className="form-label">
//                     Package Name <span className="text-danger">*</span>
//                   </label>

//                   <input
//                     name="package_name"
//                     value={formData.package_name}
//                     onChange={handleChange}
//                     className={`form-control ${errors.package_name ? "is-invalid" : ""}`}
//                   />

//                   {errors.package_name && (
//                     <div className="invalid-feedback">
//                       {errors.package_name}
//                     </div>
//                   )}
//                 </div>

//                 {/* Package Code */}
//                 <div className="col-md-4 mb-3">
//                   <label className="form-label">
//                     Package Code <span className="text-danger">*</span>
//                   </label>

//                   <input
//                     name="package_code"
//                     value={formData.package_code}
//                     onChange={handleChange}
//                     className={`form-control ${errors.package_code ? "is-invalid" : ""}`}
//                   />

//                   {errors.package_code && (
//                     <div className="invalid-feedback">
//                       {errors.package_code}
//                     </div>
//                   )}
//                 </div>

//                 {/* GEO */}
//                 <div className="col-md-4 mb-3">
//                   <label className="form-label">
//                     GEO
//                     <span className="text-danger">*</span>
//                   </label>

//                   <select
//                     name="geo_id"
//                     value={formData.geo_id}
//                     onChange={handleChange}
//                     className={`form-select ${errors.geo_id ? "is-invalid" : ""}`}
//                   >
//                     <option value="">Select GEO</option>

//                     {countries.map((item: any) => (
//                       <option key={item.country_id} value={item.country_id}>
//                         {item.country_name}
//                       </option>
//                     ))}
//                   </select>
//                   {errors.geo_id && (
//                     <div className="invalid-feedback">{errors.geo_id}</div>
//                   )}
//                 </div>

//                 {/* Scope of Package */}
//                 <div className="col-md-12 mb-3">
//                   <label className="form-label">
//                     Scope of Package
//                     <span className="text-danger">*</span>
//                   </label>

//                   <input value={sop} disabled className="form-control" />
//                 </div>
//               </div>

//               <hr
//                 style={{
//                   border: "none",
//                   height: "2px",
//                   background:
//                     "linear-gradient(to right, transparent, #6d3300, #af5d00, #f26522, #f26522, #6d3300, transparent)",
//                   borderRadius: "999px",
//                   opacity: 1,
//                   marginTop: "30px",
//                   marginBottom: "30px",
//                 }}
//               />

//               <div>
//                 <h5 className="fw-bold mb-1">Package Checks</h5>
//                 <small className="text-muted">
//                   Locked checks cannot be changed. You can only add new checks.
//                 </small>

//                 <hr
//                   style={{
//                     border: "none",
//                     borderTop: "1.5px solid #6c757d",
//                     marginTop: "10px",
//                     marginBottom: "10px",
//                   }}
//                 />

//                 <div className="card-body">
//                   {renderGroup(
//                     "Non-Assisted Checks",
//                     "Locked checks are disabled. Select additional non-assisted checks.",
//                     nonAssistedChecks,
//                   )}
//                   {renderGroup(
//                     "Assisted Checks",
//                     "Locked checks are disabled. Select additional assisted checks.",
//                     assistedChecks,
//                   )}

                  
//                 </div>
//               </div>

//               <div className="d-flex justify-content-end gap-2">
//                 <button
//                   type="button"
//                   className="btn btn-outline-primary"
//                   onClick={() => navigate(all_routes.package)}
//                 >
//                   Back
//                 </button>

//                 <button
//                   type="button"
//                   className="btn btn-primary"
//                   onClick={handleSubmit}
//                 >
//                   Update
//                 </button>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>

//       <Footer />
//     </div>
//   );
// };

// export default EditPackage; 